import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { setDefaultWatermarkEnabled } from "@/lib/aiProvenance";

interface Workspace {
  id: string;
  name: string;
  subscription_tier: string;
  seat_limit: number;
  billing_status: string;
  /** Default for the visible AI-disclosure watermark on exports (EU AI Act Art. 50). */
  ai_watermark_enabled: boolean;
  /** Tenant-level Studio switches; shape mirrors StudioConfig in useStudioSession. */
  studio_config?: Record<string, unknown>;
}

interface WorkspaceMembership {
  id: string;
  workspace_id: string;
  user_id: string;
  role: string;
  status: string;
  is_default: boolean;
}

interface Region {
  region_key: string;
  display_name: string;
  bundle_key: string | null;
  timezone_default: string | null;
}

interface UnlockedRegion {
  region_key: string;
  source: string;
}

interface WorkspaceContextType {
  workspace: Workspace | null;
  membership: WorkspaceMembership | null;
  workspaces: Workspace[];
  unlockedRegions: UnlockedRegion[];
  allRegions: Region[];
  isAdmin: boolean;
  loading: boolean;
  switchWorkspace: (workspaceId: string) => void;
  refreshWorkspace: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);

  // Mirror the workspace's AI-watermark preference into the download library,
  // which is plain (non-React) code and can't read context. Falls back to
  // enabled so an export is never silently unmarked.
  useEffect(() => {
    setDefaultWatermarkEnabled(workspace?.ai_watermark_enabled ?? true);
  }, [workspace?.ai_watermark_enabled]);
  const [membership, setMembership] = useState<WorkspaceMembership | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [unlockedRegions, setUnlockedRegions] = useState<UnlockedRegion[]>([]);
  const [allRegions, setAllRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = membership?.role === "admin" || membership?.role === "owner";

  useEffect(() => {
    if (user) {
      loadWorkspaces();
      loadAllRegions();
    } else {
      setWorkspace(null);
      setMembership(null);
      setWorkspaces([]);
      setUnlockedRegions([]);
      setLoading(false);
    }
  }, [user]);

  const loadAllRegions = async () => {
    const { data } = await supabase
      .from("regions")
      .select("*")
      .order("display_name");
    if (data) setAllRegions(data);
  };

  const loadWorkspaces = async () => {
    if (!user) return;

    // Get all active, non-deleted memberships for this user
    const { data: memberships } = await supabase
      .from("workspace_memberships")
      .select("*")
      .eq("user_id", user.id)
      .eq("status", "active")
      .eq("is_deleted", false);

    if (!memberships || memberships.length === 0) {
      setLoading(false);
      return;
    }

    // Get all non-deleted workspaces
    const workspaceIds = memberships.map((m) => m.workspace_id);
    const { data: ws } = await supabase
      .from("workspaces")
      .select("*")
      .in("id", workspaceIds)
      .eq("is_deleted", false);

    if (ws) {
      setWorkspaces(ws);

      // Priority: localStorage > is_default membership > first workspace
      const storedWsId = localStorage.getItem(`workspace_${user.id}`) || localStorage.getItem(`zitiu_workspace_${user.id}`);
      const defaultMembership = memberships.find((m) => m.is_default);
      const activeWs =
        ws.find((w) => w.id === storedWsId) ||
        (defaultMembership && ws.find((w) => w.id === defaultMembership.workspace_id)) ||
        ws[0];
      const activeMembership = memberships.find((m) => m.workspace_id === activeWs.id);

      setWorkspace(activeWs);
      setMembership(activeMembership || null);

      if (activeWs) {
        await loadUnlockedRegions(activeWs.id);
      }
    }

    setLoading(false);
  };

  const loadUnlockedRegions = async (workspaceId: string) => {
    const { data } = await supabase
      .from("workspace_regions")
      .select("region_key, source")
      .eq("workspace_id", workspaceId)
      .eq("status", "active");
    if (data) setUnlockedRegions(data);
  };

  const switchWorkspace = (workspaceId: string) => {
    const ws = workspaces.find((w) => w.id === workspaceId);
    if (ws && user) {
      setWorkspace(ws);
      localStorage.setItem(`workspace_${user.id}`, workspaceId);
      loadUnlockedRegions(workspaceId);
    }
  };

  const refreshWorkspace = async () => {
    if (workspace) {
      const { data } = await supabase
        .from("workspaces")
        .select("*")
        .eq("id", workspace.id)
        .single();
      if (data) setWorkspace(data);
    }
  };

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        membership,
        workspaces,
        unlockedRegions,
        allRegions,
        isAdmin,
        loading,
        switchWorkspace,
        refreshWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}
